// ── INVENTORY IDENTITY + RECONCILIATION ───────────────────────────────────
// Stock quantity is STORED (product.balance) and mutated, which is acceptable —
// but only if every movement is matched by a STABLE productId (not a display
// name that can be renamed or differ in casing) and if the stored figure can be
// checked against, and rebuilt from, the transaction history.
//
// This module provides the pure identity + reconciliation logic:
//   • lineMatchesProduct / resolveProductId — match movements by productId, with
//     a normalized-name fallback for legacy rows (no productId).
//   • replayProductQty — the quantity implied by opening + purchases − sales
//     + customer returns − supplier returns ± adjustments.
//   • reconcileProductStock — stored vs replay per product, with a status.
//   • rebuildStoredStock — a preview of stored-quantity corrections (never
//     touches WAC or transaction records; never auto-fixes unresolved legacy).
//
// Pure: no React, no storage. Locked down by inventory.test.js.

import { normProductName } from './moneyMath';

const num = (v) => { const n = parseFloat(v); return Number.isFinite(n) ? n : 0; };
const hasId = (v) => v !== undefined && v !== null && v !== '';

// Stable identity key for a movement line or a product.
export const productKeyOf = (o) => {
  if (!o) return 'name:';
  if (hasId(o.productId)) return `id:${o.productId}`;
  if (hasId(o.id) && o.productName === undefined && o.name !== undefined) return `id:${o.id}`; // a product record
  return `name:${normProductName(o.productName || o.name)}`;
};

// Does a movement line refer to this product? productId first, else normalized
// name. Product identity is `product.id`; a line's identity is `line.productId`.
export const lineMatchesProduct = (line, product) => {
  if (!line || !product) return false;
  if (hasId(line.productId) && hasId(product.id)) return String(line.productId) === String(product.id);
  return normProductName(line.productName || line.name) === normProductName(product.productName || product.name);
};

// Resolve a (possibly legacy) line to a productId.
//   { productId, status }
//   status: 'ok'                — line already has a productId, OR exactly one
//                                 product matches its normalized name.
//           'unresolved_legacy' — no name, no match, or MORE THAN ONE product
//                                 shares the normalized name (ambiguous — never
//                                 guess).
export const resolveProductId = (line, products = []) => {
  if (line && hasId(line.productId)) return { productId: line.productId, status: 'ok' };
  const target = normProductName(line && (line.productName || line.name));
  if (!target) return { productId: null, status: 'unresolved_legacy' };
  const matches = (products || []).filter((p) => normProductName(p.productName || p.name) === target);
  if (matches.length === 1) return { productId: matches[0].id, status: 'ok' };
  return { productId: null, status: 'unresolved_legacy' };
};

// ── Movement item extractors (preserve productId when present) ─────────────
const saleItemsOf = (s) =>
  Array.isArray(s && s.items) && s.items.length > 0
    ? s.items
    : [{ productName: s && s.productName, quantity: s && s.quantity, productId: s && s.productId, type: s && s.isService ? 'service' : 'product' }];

const purchaseItemsOf = (p) =>
  Array.isArray(p && p.items) && p.items.length > 0
    ? p.items
    : [{ productName: p && p.productName, quantity: p && p.quantity, productId: p && p.productId }];

const returnItemsOf = (r) =>
  Array.isArray(r && r.items) && r.items.length > 0
    ? r.items
    : [{ productName: r && r.productName, qty: (r && (r.qty != null ? r.qty : r.quantity)), productId: r && r.productId }];

// Quantity implied by the full transaction history for one product.
//   opening + Σ purchases − Σ sales + Σ customer returns − Σ supplier returns
//   ± Σ stock adjustments
export const replayProductQty = (product, {
  purchases = [], sales = [], salesReturns = [], purchaseReturns = [], stockAdjustments = [],
} = {}) => {
  let qty = num(product && product.openingStockQty);
  const m = (line) => lineMatchesProduct(line, product);
  purchases.forEach((pu) => purchaseItemsOf(pu).forEach((it) => { if (m(it)) qty += num(it.quantity); }));
  sales.forEach((s) => saleItemsOf(s).forEach((it) => { if (it.type === 'service') return; if (m(it)) qty -= num(it.quantity); }));
  salesReturns.forEach((r) => returnItemsOf(r).forEach((it) => { if (m(it)) qty += num(it.qty != null ? it.qty : it.quantity); }));
  purchaseReturns.forEach((r) => returnItemsOf(r).forEach((it) => { if (m(it)) qty -= num(it.qty != null ? it.qty : it.quantity); }));
  stockAdjustments.forEach((a) => {
    if (!m(a)) return;
    const q = num(a.qty != null ? a.qty : a.quantity);
    qty += (a.direction === 'decrease' || a.type === 'decrease') ? -q : q;
  });
  return qty;
};

const storedQtyOf = (p) => num(p.balance != null ? p.balance : (p.currentStock != null ? p.currentStock : p.openingStockQty));

// Customer returns whose lines carry NO snapshot cost (unitCost) — their
// inventory value was restored at the CURRENT WAC as a legacy fallback, which
// may not equal the original sale cost. Surface these so they are not presented
// as exact historical costing.
export const returnsWithApproxCost = (salesReturns = []) =>
  (salesReturns || []).filter((r) => {
    const items = Array.isArray(r && r.items) && r.items.length > 0 ? r.items : [{ type: r && r.type, unitCost: r && r.unitCost }];
    return items.some((it) => it && it.type !== 'service' && (it.unitCost == null || it.unitCost === ''));
  });

// Reconcile stored quantity against the replay, per product.
//   status: 'ok' | 'mismatch' | 'unresolved_legacy'
// A product whose normalized name is shared by another product is
// 'unresolved_legacy' (legacy name-matched movements can't be safely attributed).
export const reconcileProductStock = ({
  products = [], purchases = [], sales = [], salesReturns = [], purchaseReturns = [], stockAdjustments = [], tolerance = 0.001,
} = {}) => {
  const nameCounts = {};
  (products || []).forEach((p) => {
    const k = normProductName(p.productName || p.name);
    nameCounts[k] = (nameCounts[k] || 0) + 1;
  });
  return (products || []).map((p) => {
    const storedQty = storedQtyOf(p);
    const replayQty = replayProductQty(p, { purchases, sales, salesReturns, purchaseReturns, stockAdjustments });
    const difference = Math.round((storedQty - replayQty) * 1e6) / 1e6;
    // Two products sharing a normalized name make any name-matched (legacy)
    // movement ambiguous — never guess; flag for manual review.
    const ambiguous = nameCounts[normProductName(p.productName || p.name)] > 1;
    let status = 'ok';
    if (ambiguous) status = 'unresolved_legacy';
    else if (Math.abs(difference) > tolerance) status = 'mismatch';
    return { productId: p.id, productName: p.productName, storedQty, replayQty, difference, status };
  });
};

// Preview of stored-quantity corrections. Only 'mismatch' rows are changed;
// 'unresolved_legacy' rows are NEVER auto-fixed. Returns { changes, apply }
// where apply(products) returns a NEW products array with corrected balances.
export const rebuildStoredStock = (reconciliation = []) => {
  const changes = reconciliation
    .filter((r) => r.status === 'mismatch')
    .map((r) => ({ productId: r.productId, productName: r.productName, from: r.storedQty, to: r.replayQty }));
  const byId = new Map(changes.map((c) => [String(c.productId), c.to]));
  const apply = (products, meta = {}) =>
    (products || []).map((p) => {
      if (!byId.has(String(p.id))) return p;
      const to = byId.get(String(p.id));
      const when = meta.at || new Date().toISOString();
      return {
        ...p,
        balance: to,
        currentStock: to,
        stockRebuiltAt: when,
        updatedAt: when, // record-level conflict resolution
      };
    });
  return { changes, apply };
};
